<template>
  <el-dialog
    :visible.sync="dialogVisible"
    width="720px"
    append-to-body
    :close-on-click-modal="false"
    @closed="handleClosed"
  >
    <div slot="title" class="dialog-title">
      <span class="dialog-title-text">编辑用户资料</span>
      <span class="dialog-warning">
        <i class="el-icon-warning-outline"></i>
        请谨慎修改用户信息，保存后会同步影响相关名片和发布记录展示。
      </span>
    </div>

    <div v-loading="loading">
      <el-descriptions v-if="user" :column="2" size="small" border class="summary-block">
        <el-descriptions-item label="用户ID">{{ formatUserCode(user) }}</el-descriptions-item>
        <el-descriptions-item label="资质状态">{{ qualificationStatusText }}</el-descriptions-item>
      </el-descriptions>

      <el-alert
        v-if="loaded && !hasQualification"
        title="该用户暂无资质记录，本次只会修改基础资料，不会创建资质申请。"
        type="info"
        show-icon
        :closable="false"
        class="form-alert"
      />

      <el-form
        ref="formRef"
        :model="form"
        :rules="formRules"
        label-width="110px"
        class="edit-form"
      >
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="名称/昵称" prop="contactName">
              <el-input v-model.trim="form.contactName" clearable placeholder="请输入名称/昵称" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="联系电话" prop="contactPhone">
              <el-input v-model.trim="form.contactPhone" clearable placeholder="请输入联系电话" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="公司/主体" prop="companyName">
          <el-input v-model.trim="form.companyName" clearable placeholder="请输入公司或主体名称" />
        </el-form-item>

        <template v-if="hasQualification">
          <el-row :gutter="16">
            <el-col :span="12">
              <el-form-item label="认证类型" prop="applicantType">
                <el-select v-model="form.applicantType" placeholder="请选择认证类型" style="width: 100%;">
                  <el-option label="企业" value="company" />
                  <el-option label="个人" value="individual" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item :label="idNumberLabel" prop="idNumber">
                <el-input v-model.trim="form.idNumber" clearable :placeholder="idNumberPlaceholder" />
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="16">
            <el-col :span="12">
              <el-form-item label="经营类目" prop="category">
                <el-input v-model.trim="form.category" clearable placeholder="请输入经营类目" />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="经营地址" prop="address">
                <el-input v-model.trim="form.address" clearable placeholder="请输入经营地址" />
              </el-form-item>
            </el-col>
          </el-row>

          <el-form-item label="补充说明" prop="description">
            <el-input
              v-model.trim="form.description"
              type="textarea"
              :rows="3"
              maxlength="1000"
              show-word-limit
              placeholder="请输入补充说明"
            />
          </el-form-item>

          <el-form-item label="资质图片" prop="images">
            <el-upload
              action=""
              list-type="picture-card"
              accept="image/*"
              :file-list="imageFileList"
              :http-request="handleImageUpload"
              :before-upload="beforeImageUpload"
              :on-preview="handleImagePreview"
              :on-remove="handleImageRemove"
              :disabled="imageUploading || form.images.length >= maxImages"
            >
              <i class="el-icon-plus"></i>
            </el-upload>
            <div class="upload-tip">最多上传 {{ maxImages }} 张，保存时会同步到资质资料。</div>
          </el-form-item>
        </template>
      </el-form>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="dialogVisible = false">取消</el-button>
      <el-button type="primary" :loading="saving" @click="handleSubmit">保存修改</el-button>
    </span>

    <el-dialog :visible.sync="previewVisible" append-to-body width="520px">
      <img v-if="previewImageUrl" :src="previewImageUrl" class="preview-image" alt="资质图片预览">
    </el-dialog>
  </el-dialog>
</template>

<script>
import {
  fetchQualificationDetail,
  fetchUserDetail,
  updateUserProfileQualification,
  uploadAdminImage
} from '../api/admin'
import { isAuthRedirectError } from '../utils/auth'

const EMPTY_FORM = {
  contactName: '',
  contactPhone: '',
  companyName: '',
  applicantType: 'company',
  idNumber: '',
  category: '',
  address: '',
  description: '',
  images: []
}

export default {
  name: 'UserProfileQualificationEditDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    openid: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      saving: false,
      imageUploading: false,
      loaded: false,
      user: null,
      qualificationDetail: null,
      form: { ...EMPTY_FORM },
      imageFileList: [],
      maxImages: 5,
      previewVisible: false,
      previewImageUrl: ''
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(value) {
        this.$emit('update:visible', value)
      }
    },
    currentRecord() {
      return this.qualificationDetail && this.qualificationDetail.record ? this.qualificationDetail.record : null
    },
    hasQualification() {
      return !!this.currentRecord
    },
    qualificationStatusText() {
      const status = (this.currentRecord && this.currentRecord.status) ||
        (this.user && this.user.qualificationStatus) ||
        'none'
      return this.qualificationStatusLabel(status)
    },
    idNumberLabel() {
      return this.form.applicantType === 'individual' ? '身份证号' : '信用代码'
    },
    idNumberPlaceholder() {
      return this.form.applicantType === 'individual' ? '请输入身份证号' : '请输入统一社会信用代码'
    },
    formRules() {
      const required = (message) => [{ required: true, message, trigger: 'blur' }]
      const imageRule = {
        validator: (rule, value, callback) => {
          if (!this.hasQualification) return callback()
          if (!this.form.images.length) return callback(new Error('请至少上传 1 张资质图片'))
          if (this.form.images.length > this.maxImages) return callback(new Error(`资质图片最多上传 ${this.maxImages} 张`))
          callback()
        },
        trigger: 'change'
      }
      return this.hasQualification
        ? {
            companyName: required('请输入公司或主体名称'),
            contactPhone: required('请输入联系电话'),
            applicantType: required('请选择认证类型'),
            idNumber: required('请输入证件号'),
            category: required('请输入经营类目'),
            images: [imageRule]
          }
        : {}
    }
  },
  watch: {
    visible(value) {
      if (value) {
        this.loadDetail()
      }
    },
    openid(value, oldValue) {
      if (this.visible && value && value !== oldValue) {
        this.loadDetail()
      }
    }
  },
  methods: {
    resetState() {
      this.loaded = false
      this.user = null
      this.qualificationDetail = null
      this.form = { ...EMPTY_FORM, images: [] }
      this.imageFileList = []
      this.previewVisible = false
      this.previewImageUrl = ''
      this.$nextTick(() => {
        if (this.$refs.formRef) {
          this.$refs.formRef.clearValidate()
        }
      })
    },
    async loadDetail() {
      if (!this.openid) return
      this.loading = true
      this.resetState()
      try {
        const [user, qualificationDetail] = await Promise.all([
          fetchUserDetail(this.openid),
          fetchQualificationDetail(this.openid)
        ])
        this.user = user
        this.qualificationDetail = qualificationDetail
        this.populateForm()
        this.loaded = true
      } catch (error) {
        if (isAuthRedirectError(error)) return
        this.$message.error(error.message || '用户资料获取失败')
      } finally {
        this.loading = false
      }
    },
    populateForm() {
      const record = this.currentRecord
      const images = record && Array.isArray(record.images) ? record.images.filter(Boolean) : []
      this.form = {
        contactName: this.user && this.user.contactName ? this.user.contactName : '',
        contactPhone: record && record.phone ? record.phone : (this.user && this.user.contactPhone ? this.user.contactPhone : ''),
        companyName: record && record.companyName ? record.companyName : (this.user && this.user.companyName ? this.user.companyName : ''),
        applicantType: record && record.applicantType ? record.applicantType : 'company',
        idNumber: record && record.idNumber ? record.idNumber : '',
        category: record && record.category ? record.category : '',
        address: record && record.address ? record.address : '',
        description: record && record.description ? record.description : '',
        images: [...images]
      }
      this.imageFileList = images.map((url, index) => ({
        name: `资质图片${index + 1}`,
        url
      }))
    },
    handleClosed() {
      this.resetState()
    },
    beforeImageUpload(file) {
      if (!file || !String(file.type || '').startsWith('image/')) {
        this.$message.warning('只能上传图片文件')
        return false
      }
      if (this.form.images.length >= this.maxImages) {
        this.$message.warning(`资质图片最多上传 ${this.maxImages} 张`)
        return false
      }
      return true
    },
    async handleImageUpload(options) {
      const file = options && options.file
      this.imageUploading = true
      try {
        const result = await uploadAdminImage(file, 'qualification')
        const url = result && (result.url || result.imageUrl || result.key || result.imageKey)
        if (!url) {
          throw new Error('图片上传失败：未返回图片地址')
        }
        this.form.images.push(url)
        this.imageFileList.push({
          name: file && file.name ? file.name : `资质图片${this.form.images.length}`,
          url
        })
        if (options && options.onSuccess) options.onSuccess(result)
        if (this.$refs.formRef) {
          this.$refs.formRef.validateField('images')
        }
      } catch (error) {
        if (options && options.onError) options.onError(error)
        this.$message.error(error.message || '图片上传失败')
      } finally {
        this.imageUploading = false
      }
    },
    handleImageRemove(file) {
      const url = file && file.url
      if (!url) return
      this.form.images = this.form.images.filter(item => item !== url)
      this.imageFileList = this.imageFileList.filter(item => item.url !== url)
      if (this.$refs.formRef) {
        this.$refs.formRef.validateField('images')
      }
    },
    handleImagePreview(file) {
      this.previewImageUrl = file && file.url ? file.url : ''
      this.previewVisible = !!this.previewImageUrl
    },
    handleSubmit() {
      if (this.saving) return
      if (!this.$refs.formRef) return
      this.$refs.formRef.validate(async (valid) => {
        if (!valid) return
        try {
          await this.$confirm(
            '用户资料修改后会同步影响该用户的名片、联系方式、公司资料以及已有销售/求购发布记录展示，是否确认保存修改？',
            '确认修改用户信息',
            {
              confirmButtonText: '确认修改',
              cancelButtonText: '取消',
              type: 'warning'
            }
          )
        } catch (error) {
          return
        }
        this.saving = true
        try {
          const result = await updateUserProfileQualification(this.openid, {
            contactName: this.form.contactName,
            contactPhone: this.form.contactPhone,
            companyName: this.form.companyName,
            applicantType: this.form.applicantType,
            idNumber: this.form.idNumber,
            category: this.form.category,
            address: this.form.address,
            description: this.form.description,
            images: this.form.images
          })
          const updatedProductCount = Number(result && result.updatedProductCount)
          const productText = Number.isFinite(updatedProductCount) && updatedProductCount > 0
            ? `，已同步 ${updatedProductCount} 条发布记录`
            : ''
          this.$message.success(`资料修改成功${productText}`)
          this.$emit('saved', result)
          this.dialogVisible = false
        } catch (error) {
          if (isAuthRedirectError(error)) return
          this.$message.error(error.message || '资料修改失败')
        } finally {
          this.saving = false
        }
      })
    },
    formatUserCode(user) {
      const directCode = String((user && user.userCode) || '').trim()
      if (directCode) return directCode
      const userId = Number(user && user.userId)
      if (!Number.isInteger(userId) || userId <= 0) return '--'
      return userId < 1000 ? String(userId).padStart(3, '0') : String(userId)
    },
    qualificationStatusLabel(status) {
      if (status === 'approved') return '已通过'
      if (status === 'rejected') return '已驳回'
      if (status === 'pending') return '待审核'
      if (status === 'none') return '未认证'
      return status || '-'
    }
  }
}
</script>

<style scoped>
.dialog-title {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.dialog-title-text {
  color: #1f2937;
  font-size: 18px;
  font-weight: 500;
}

.dialog-warning {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  color: #b45309;
  background: #fffbeb;
  border: 1px solid #fde68a;
  font-size: 12px;
  line-height: 20px;
}

.summary-block {
  margin-bottom: 14px;
}

.form-alert {
  margin-bottom: 14px;
}

.edit-form {
  margin-top: 4px;
}

.upload-tip {
  margin-top: 6px;
  color: #7d88a3;
  font-size: 12px;
}

.preview-image {
  display: block;
  width: 100%;
  max-height: 70vh;
  object-fit: contain;
}
</style>
